"use client";

import React, { useEffect } from "react";

export default function BaseModal({
    title,
    children,
    isOpen: modalIsOpen,
    setIsOpen: setModalIsOpen,
    error = undefined,
    removeAction = undefined,
    actions = undefined,
    onFormSubmit = undefined,
    isFullScreen = false,
    topRightAction = undefined,
}: {
    title?: string;
    children: React.ReactNode;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    error?: string | null;
    removeAction?: React.ReactNode;
    actions?: React.ReactNode;
    onFormSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
    isFullScreen?: boolean;
    topRightAction?: React.ReactNode;
}) {
    useEffect(() => {
        if (!modalIsOpen) {
            document.body.style.overflow = "auto";
        } else {
            document.body.style.overflow = "hidden";
        }
    }, [modalIsOpen]);

    // Conditional wrapper function
    const ConditionalWrapper = ({ condition, wrapper, children }: { condition: boolean; wrapper: (children: React.ReactNode) => JSX.Element; children: React.ReactNode }) =>
        condition ? wrapper(children) : <>{children}</>;

    return (
        <div>
            {modalIsOpen ? (
                <>
                    <ConditionalWrapper
                        condition={!!onFormSubmit}
                        wrapper={(children) => (
                            <form
                                onSubmit={onFormSubmit}
                                className={`justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none text-left ${isFullScreen ? "w-full h-full p-10" : ""}`}
                            >
                                {children}
                            </form>
                        )}
                    >
                        <div
                            className={`justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none text-left ${isFullScreen ? "w-full h-full p-10" : ""}`}
                        >
                            <div className={`relative ${isFullScreen ? "w-full h-full" : "w-auto mx-auto max-w-3xl"}`}>
                                <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                                    <div className="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
                                        {title && <h3 className="text-xl font-semibold">{title}</h3>}
                                        {topRightAction && <div className="absolute top-5 right-5">{topRightAction}</div>}
                                    </div>
                                    <div className="relative p-6 flex-auto">
                                        {!isFullScreen ? (
                                            <div className="max-h-[50vh] min-w-[600px] overflow-y-auto overflow-x-hidden">
                                                {children}
                                            </div>
                                        ) : (
                                            <div style={{ maxHeight: "calc(100vh - 300px)" }} className="overflow-y-auto overflow-x-hidden">
                                                {children}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                                        <div className="flex-grow">
                                            {error && <div className="text-red-500 text-sm font-medium">{error}</div>}
                                            {removeAction}
                                        </div>
                                        {actions}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </ConditionalWrapper>
                    <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
                </>
            ) : null}
        </div>
    );
}
